import React from 'react';
import { useTranslation } from 'react-i18next';

import { Card, ToastContext, WeezHeader, Search, Button, Icon, FieldErrors, TooltipHelp } from '@weezevent/nacre';
import { LoadingCard } from '../../components';
import useDateFormat from '../../hooks/useDateFormat';

import css from './emails.module.css';
import { fetcher } from '@weezevent/weezjs-core';

export default function BlackList() {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [email, setEmail] = React.useState('');
    let [result, setResult] = React.useState(false);
    let [searchError, setSearchError] = React.useState(false);
    let [show_additional_information, setShow_additional_information] = React.useState(false);
    let [rejectedEmail, setRejectedEmail] = React.useState(null);
    let [loadingRejectedEmail, setLoadingRejectedEmail] = React.useState(false);

    const handleSearchEmail = React.useCallback(() => {
        setLoadingRejectedEmail(true);
        const url = `/mail/rejected-email/${email}`;
        fetcher
            .get(url)
            .then(response => {
                if (response.status === 200) {
                    setRejectedEmail(response.response);
                    setLoadingRejectedEmail(false);
                    setResult(true);
                }
            })
            .catch(() => {
                setLoadingRejectedEmail(false);
                setSearchError(true);
            });
    }, [rejectedEmail, email]);

    const handleDeleteEmail = React.useCallback(() => {
        const url = `/mail/rejected-email/${email}`;
        fetcher
            .delete(url)
            .then(response => {
                if (response.status === 200) {
                    setEmail('');
                    setSearchError(false);
                    setResult(false);
                    toast.success(t('sauron.emails.blacklist.remove-success'));
                }
            })
            .catch(() => {
                toast.error(t('sauron.emails.blacklist.remove-error'));
            });
    }, [email]);

    let handleSearch = React.useCallback(
        value => {
            setEmail(value);
        },
        [email]
    );

    let handleCancel = React.useCallback(() => {
        setResult(false);
    }, [result]);

    let showInformation = React.useCallback(() => {
        setShow_additional_information(!show_additional_information);
    }, [show_additional_information]);

    return (
        <>
            <WeezHeader title={t('sauron.emails.blacklist.title')}>{t('sauron.emails.blacklist.subtitle')}</WeezHeader>

            {result ? (
                <>
                    <Card>
                        <div className={css['card-header']}>
                            <p className={css['card-title']}>{t('sauron.emails.blacklist.search-title')}</p>
                        </div>
                    </Card>
                    <Card>
                        <div className={css['card-header']}>
                            <p className={css['card-title']}>{t('sauron.emails.blacklist.result-title')}</p>
                            <p className={css['card-subtitle']}>{t('sauron.emails.blacklist.result-subtitle')}</p>
                        </div>
                        <div className={css['card-content']}>
                            <div className={css['elements-in-row']}>
                                <div className={css['search-element']}>
                                    <p className={css['info_label']}>{t('sauron.emails.blacklist.email-label')}</p>
                                    <p className={css['info_label']}>{t('sauron.emails.blacklist.last-update')}</p>
                                    <p className={css['info_label']}>{t('sauron.emails.blacklist.reason')}</p>
                                </div>
                                <div className={css['search-element']}>
                                    <p>{rejectedEmail.email} </p>
                                    <p>
                                        {rejectedEmail.last_update !== 'Unknown' ? useDateFormat({ date: rejectedEmail.last_update }).properDateAndTime : rejectedEmail.last_update}
                                    </p>
                                    <div>
                                        {rejectedEmail.reason}
                                        {rejectedEmail.reason !== 'Unknown' && (
                                            <TooltipHelp
                                                className={css['reason-tooltip']}
                                                position={'right'}
                                                title={
                                                    rejectedEmail.reason === 'BOUNCE'
                                                        ? t('sauron.emails.blacklist.bounce-text-info')
                                                        : t('sauron.emails.blacklist.complaint-text-info')
                                                }
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                            <p className={css['additional-info-button']} onClick={showInformation}>
                                {show_additional_information ? <Icon name="minus" /> : <Icon name="plus" />}
                                {t('sauron.emails.blacklist.additional-information')}
                            </p>
                            {show_additional_information && <div className={css['additional-information']}>{JSON.stringify(rejectedEmail.additional_information, null, 4)}</div>}
                        </div>
                        <div className={css['card-footer']}>
                            <Button label={t('sauron.emails.blacklist.remove-button')} onClick={handleDeleteEmail} primary />
                            <Button inverted={true} label={t('common.cta.cancel')} primary onClick={handleCancel} />
                        </div>
                    </Card>
                </>
            ) : (
                <>
                    <Card>
                        <div className={css['card-header']}>
                            <p className={css['card-title']}>{t('sauron.emails.blacklist.search-title')}</p>
                            <p className={css['card-subtitle']}>{t('sauron.emails.blacklist.search-subtitle')}</p>
                        </div>
                        <div className={css['card-content']}>
                            <label>{t('sauron.emails.blacklist.email-label')}</label>
                            <div style={{ width: '50%' }}>
                                <Search
                                    errors={searchError}
                                    handleReset={() => handleSearch('')}
                                    icon="search"
                                    onChange={(_, value) => handleSearch(value)}
                                    placeholder={t('sauron.emails.blacklist.email-placeholder')}
                                    value={email}
                                />
                                {searchError && <FieldErrors className="" errors={[t('sauron.emails.blacklist.error-message')]} />}
                            </div>
                        </div>
                        <div className={css['card-footer']}>
                            <Button disabled={email === ''} onClick={() => handleSearchEmail()} label={t('common.search')} primary />
                        </div>
                    </Card>
                    <Card>
                        <div className={css['card-header']}>
                            <p className={css['card-title']}>{t('sauron.emails.blacklist.result-title')}</p>
                            <LoadingCard loading={loadingRejectedEmail} />
                        </div>
                    </Card>
                </>
            )}
        </>
    );
}
